<template>
	<div
		class="chat-panel"
		:class="{ focused: store.appVisible }"
		ref="chatPanelContainer"
	>
		<DropZone :scoped="subscriptionType === 'thread'" @drop="onDropFiles" />
		<LoadingBar v-show="loadingMessages" />

		<AccessibleSection
			v-if="showPinnedBanner"
			:name="idPrefix + 'pinned-message'"
		>
			<PinnedMessage :chatId="chatId" @click="pinnedBannerClick" />
		</AccessibleSection>

		<AccessibleSection
			:name="idPrefix + 'chat-messages'"
			class="message-list-wrapper"
			focusOnFirstItem
			:tabOrder="[{ css: '.accessible-item' }]"
			:class="{ 'no-scroll': loadingMessages && messages.length === 0 }"
		>
			<UserListDrawer
				v-if="showListDrawer"
				:userIds="userList.ids"
				:label="userList.label"
				:bubbleId="chatId"
				@close="showListDrawer = false"
			/>
			<MessageList
				:chatId="chatId"
				:messages="messages"
				:isLoadingMore="messagesLoaded && loadingMessages && initialLoad"
				:readReceiptsByMessageId="readReceiptsByMessageId"
				class="message-list-main"
				:class="{ 'hide-chat': threadId }"
				:isListVisible="true"
				:allowLoadForward="!forwardHistoryComplete"
				:lastViewedId="lastViewedId"
				:isJumpingToLatest="isJumpingToLatest"
				:isLoading="loadingMessages && messages.length === 0"
				:hideCreateThread="hideCreateThread"
				:idPrefix="idPrefix"
				@ref="messageList = $event"
				@load-backward="handleLoadBackward"
				@load-forward="handleLoadForward"
				@thread="openThread"
				@reply="openReplyThread"
				@create-thread="openCreateThread"
				@jump-to-latest="jumpToLatest"
				@viewed-all-unreads="dismissUnreadBanner"
				:historyComplete="historyComplete"
			/>
			<ReplyChainView
				v-if="threadId"
				:replyChainId="threadId"
				:bubbleId="chatId"
				:typingUsers="typingUsers"
				:pinView="pinView"
				@close="threadId = null"
				@reply="openReplyThread"
			/>
		</AccessibleSection>

		<div class="status-bar-wrapper">
			<div
				class="status-bar"
				:class="{ 'status-bar-expanded': !threadId && showTypingIndicator }"
				v-text="typingText"
			/>
		</div>

		<AccessibleSection
			:name="idPrefix + 'chat-input'"
			:tabOrder="[
				{
					css: '.media-container',
				},
				{
					css: '.pronto-editor',
					clickOnEnter: true,
					Enter: () => {
						focusEditorExitAccessibility()
					},
				},
				{
					css: '.message-input-actions .btn-file-upload',
					clickOnEnter: true,
				},
				{
					css: '.message-input-actions .emojis-icon',
				},
				{
					css: '.message-input-actions .gifs .giphy-button',
					clickOnEnter: true,
				},
			]"
			class="message-footer shadow-left"
		>
			<MessageInput
				:parentId="threadId"
				:chatIdOverride="chatId"
				:mentionChatId="mentionChatId"
				:beforeSend="beforeSendHandler"
				:placeholderOverride="placeholderOverride"
				:compact="compact"
				:pasteChannel="
					subscriptionType === 'thread'
						? 'paste-files-to-thread'
						: 'paste-files-to-chat'
				"
				@keyup="typingHandler"
				@messageSent="messageSentHandler"
				@messageCreated="messageCreatedHandler"
				ref="messageInput"
			/>
		</AccessibleSection>

		<NewMessageButton
			v-if="showUnreadBanner"
			@click="scrollToLatestUnread"
			@dismiss="dismissUnreadBanner"
			:new-message-loader="newMessageLoader"
			:message_num="unreadCount"
			:class="{ 'pinned-banner': showPinnedBanner }"
		/>

		<div
			aria-live="assertive"
			role="status"
			ref="screenreader"
			style="opacity: 0; position: absolute; pointer-events: none"
		/>

		<div v-if="showFail" class="error-wrapper">
			<div class="error-alert">
				<span>{{ alertText }}</span
				><span
					v-if="showRefresh"
					style="text-decoration: underline; cursor: pointer; margin-left: 10px"
					alt="retry"
					@click="reloadPage()"
					>{{ $t('chat_fail_retry') }}</span
				>
				<input
					type="image"
					src="/images/common/white-icon-close.png"
					@click="clearFail()"
				/>
			</div>
		</div>

		<SuperGroupModal
			v-if="showSuperGroupModal"
			:chatId="chatId"
			@close="dismissSuperGroupAlert"
		/>
	</div>
</template>

<script>
import store, {
	getMessages,
	getMessagesForBubble,
	getMessageIds,
	getMessageIdsForBubble,
	deleteMessage,
	getOldestMessage,
	setMessage,
	setMessages,
	updateMessage,
	getMemberships,
	getMembershipsForBubble,
	getMyMembershipForBubble,
	updateMembershipForUser,
	isMyUser,
	deleteOldestMessagesForBubble,
	getMostRecentMessage,
	getMostRecentMessageForBubble,
	getMyUserId,
	getStartCursorForBubble,
	getStopCursorForBubble,
	setMessageAndMoveCursor,
	getSortedMessageIdsForBubble,
	setStopCursorForBubble,
	setStartCursorForBubble,
	importMessagesFromCacheForBubble,
	getChats,
	getChat,
	setVideosession,
	updateStat,
	getVideosession,
	getStat,
	getMyUser,
	getMessage,
} from '@/store'
import { post, getCancelToken, isCanceled } from '@/helpers/api'
import { closeNotificationsForBubble } from '@/helpers/pushNotification'

import MessageInput from '@/components/MessageInput'
import DropZone from '@/components/Home/DropZone'
import NewMessageButton from '@/components/NewMessageButton'
import LoadingBar from '@/components/LoadingBar'
import MessageList from './MessageList'
import PinnedMessage from './PinnedMessage'
import ReplyChainView from './ReplyChainView'
import SuperGroupModal from '@/components/Home/SuperGroupModal'
import { asyncIsElementInView, getTypingIndicatorText } from '@/helperMethods'
import debounce from 'debounce'
import {
	subscribeToPrivateBubble,
	retainChannel,
	releaseChannel,
	getChannelRefCount,
	getBindEpoch,
	unbindChannel,
	bindChannelAppEvents,
	bindChannelRestEvents,
	bindChannelClientEvents,
} from '@/helpers/pusher'
import {
	fetchChat,
	fetchHistory,
	fetchHistoryWithoutStore,
	fetchMyMembershipForChat,
} from '@/api/chat'
import { useCenteredMessage } from '@/composables/useCenteredMessage'
import { markChatAsRead, updateMembership } from '@/api/membership'
import {
	MESSAGE_PAGE_SIZE,
	USER_TYPING_EVENT_INTERVAL_MS,
} from '@/helpers/constants'
import UserListDrawer from '@/components/Message/UserListDrawer'
import { sleep, storage, sStorage, trackEvent } from '@/helpers/utils'
import { AccessibleSection } from '@/components/common/Accessible'
import { closeAllPopovers } from '../common/Popover'
import { createLogger, timedLogger } from '@/helpers/log'
import { updateMessageWithPoll } from '@/store/messages'
import { joinThread } from '@/api/thread'

const log = createLogger('ChatPanel')

export default {
	name: 'ChatPanel',

	components: {
		MessageInput,
		DropZone,
		NewMessageButton,
		LoadingBar,
		MessageList,
		ReplyChainView,
		UserListDrawer,
		AccessibleSection,
		SuperGroupModal,
		PinnedMessage,
	},

	props: {
		chatId: { type: Number, required: true },
		subscriptionType: {
			type: String,
			default: 'main',
			validator: (v) => ['main', 'thread'].includes(v),
		},
		hideCreateThread: { type: Boolean, default: false },
		mentionChatId: { type: Number, default: null },
		idPrefix: { type: String, default: '' },
		centeredMessageId: { type: Number, default: null },
		placeholderOverride: { type: String, default: null },
		compact: { type: Boolean, default: false },
	},

	emits: ['loaded', 'create-thread'],

	setup() {
		return { centered: useCenteredMessage() }
	},

	data() {
		return {
			store,
			messages: [],
			threadId: null,
			mark: 0,
			initialMark: null,
			loaded: false,
			messagesLoaded: true,
			loadingMessages: true,
			historyComplete: false,
			pusher: {
				channelName: null,
				channelCode: null,
				channel: null,
			},
			typingText: null,
			typingUsers: {},
			showTypingIndicator: false,
			lastTypingSentTime: 0,
			unreadCount: 0,
			currentMark: 0,
			pendingMark: 0,
			latest_message_id: 0,
			newMessageLoader: false,
			showListDrawer: false,
			userList: null,
			loadHistoryCancelToken: null,
			showFail: false,
			showRefresh: false,
			alertText: false,
			connectedToPusher: false,
			initialLoad: false,
			readReceiptsByMessageId: {},
			centeredId: null,
			forwardHistoryComplete: false,
			hasPreviouslyConnected: false,
			isJumpingToLatest: false,
			showSuperGroupModal: false,
			waitingForBannerCheck: false,
			channelName: null,
			hasThreadMembership: true,
		}
	},

	watch: {
		chatId: {
			immediate: true,
			handler(newId, oldId) {
				if (newId && newId !== oldId) {
					this.load(newId)
				}
			},
		},

		'store.appVisible': {
			handler() {
				if (this.store.appVisible && this.loaded) {
					this.updateMark()
				}
			},
			deep: true,
		},

		'store.userIsActive': {
			handler(val) {
				if (val) {
					this.updateMark()
				}
			},
			deep: true,
		},

		currentChat: function (val, oldVal) {
			this.$nextTick(() => {
				if (oldVal) {
					if (val.id === oldVal.id) {
						if (val.channelcode !== oldVal.channelcode) {
							releaseChannel(this.channelName)
							this.connectPusher(() => {
								console.debug('reconnecting to new pusher channel')
							})
						}
					}
				}
			})
		},

		threadId(id, old) {
			if (old && !id) {
				// thread view was just closed
				EventBus.$emit('clear-prepend-mention')
				this.updateMark()
			}
			EventBus.$emit('thread-change', id)
		},

		initialLoad(loaded) {
			if (loaded) {
				this.messageList.scrollTop = 0
			}
		},

		showListDrawer(show) {
			if (!show) {
				this.userList = null
			}
		},

		typingUsers: {
			handler(typingUsers) {
				const userIds = Object.keys(typingUsers)
				if (userIds.length) {
					this.showTypingIndicator = true
					this.typingText = getTypingIndicatorText(userIds)
				} else {
					this.showTypingIndicator = false
					setTimeout(() => {
						// delay for better animation
						this.typingText = null
					}, 300)
				}
			},
			deep: true,
		},
	},

	computed: {
		currentChat() {
			return getChat(this.chatId)
		},

		lastViewedId() {
			if (!this.initialMark || !this.latest_message_id) return null
			if (this.initialMark === this.latest_message_id) return null
			return this.initialMark
		},

		showUnreadBanner() {
			return !this.threadId && this.unreadCount > 0
		},

		showPinnedBanner() {
			return (
				!!this.currentChat?.pinned_message_id &&
				!this.threadId &&
				this.messages.length > 0 &&
				!this.hidePinnedChat
			)
		},

		hidePinnedChat() {
			const membership = getMyMembershipForBubble(this.chatId)
			return membership?.pinned_message_hidden || false
		},

		pinView() {
			return this.currentChat?.pinned_message_id === this.threadId
		},
	},

	created() {
		this.readReceiptsByMessageId = this.calculateReadReceipts()
		this.$onEventBus({
			'message-change': () => {
				this.rebuildMessages()

				if (this.waitingForBannerCheck) {
					this.waitingForBannerCheck = false
					this.$nextTick(() => {
						this.checkIfUnreadBannerShouldDisplay()
					})
				}
				this.updateMark()
			},
			'message-change|mark-update|changeBubble|membership-update': debounce(
				() => {
					this.$nextTick(() => {
						this.readReceiptsByMessageId = this.calculateReadReceipts()
					})
				},
				25
			),
			'user-typing': (data) => this.userTypingHandler(data),
			'unread-banner': (count) => {
				this.unreadCount = count
			},
			'stat-update': (stat) => {
				if (stat.bubble_id !== this.chatId) return
				Object.assign(this, {
					currentMark: stat.mark,
					latest_message_id: stat.latest_message_id,
				})
				if (!store.appVisible) {
					this.checkIfUnreadBannerShouldDisplay()
				}
			},
			reloadPusherInstance: () => {
				this.connectPusher()
			},
			'open-pinned-message': () => {
				if (this.subscriptionType !== 'main') return
				closeAllPopovers()
				this.pinnedBannerClick()
			},
		})

		this.chatInfoError = 0
		this.bubbleHistoryError = 0

		this.apiWatchdogInterval = setInterval(() => {
			if (this.chatInfoError > 0) {
				trackEvent('chat_info_retry', { count: this.chatInfoError })
				console.debug('Chat info error, retrying...', this.chatInfoError)
				this.loadChatInfo()
			}
			if (this.bubbleHistoryError > 0) {
				trackEvent('bubble_history_retry', { count: this.bubbleHistoryError })
				console.debug(
					'Bubble history error, retrying...',
					this.bubbleHistoryError
				)
				this.loadMessageHistory()
			}
		}, 10000)
	},

	mounted() {
		this.$pusher?.connection?.bind('connected', this.pusherReconnect)

		this.$onEventBus({
			focusMessageInput: () => {
				if (this.subscriptionType !== 'main') return
				this.$refs.messageInput?.setFocus()
			},
			updateUserList: (data) => {
				this.updateUserList(data)
			},
			'dormancy-refresh': () => {
				// On wake from extended dormancy the active panel may be
				// stale regardless of how Pusher behaved during the gap:
				// a silent reconnect could have re-fired 'connected' against
				// a dormant tab, or the connection stayed alive and never
				// re-fired at all. Routing through pusherReconnect is safe
				// in both cases — the 300ms debounce coalesces same-window
				// calls and the _loadGeneration counter cancels any
				// in-flight stale load(), so we end up with one fresh load.
				//
				// Force hasPreviouslyConnected=true first: pusherReconnect
				// no-ops on the first call (it only flips the flag without
				// scheduling a load). If a panel mounted during the dormancy
				// window and gets dormancy-refresh before its first
				// 'connected' event, the refresh would otherwise be
				// silently swallowed.
				console.debug(
					`[dormancy] refresh routing to pusherReconnect chat=${this.chatId}`
				)
				this.hasPreviouslyConnected = true
				this.pusherReconnect()
			},
		})
	},

	beforeUnmount() {
		this._unmounted = true
		clearInterval(this.apiWatchdogInterval)
		clearTimeout(this._reconnectTimer)
		this.$pusher?.connection?.unbind('connected', this.pusherReconnect)
		this.loadHistoryCancelToken?.cancel?.()
		this.loadHistoryCancelToken = null
		this.loadInfoAbortController?.abort?.()
		this.loadInfoAbortController = null
		this._pusherUnmountReject?.(new Error('ChatPanel unmounted'))
		this._pusherUnmountReject = null
		this.unload()
		this.messageList = null
	},

	methods: {
		sleep,

		rebuildMessages() {
			if (this.subscriptionType === 'thread') {
				const threadMessages = getMessagesForBubble(this.chatId)
				if (this.historyComplete) {
					const chat = getChat(this.chatId)
					const sourceMessage = chat.source_message_id
						? getMessage(chat.source_message_id)
						: null
					if (sourceMessage) {
						this.messages = [
							{ ...sourceMessage, thread_id: null, isThreadSource: true },
							...threadMessages,
						]
						return
					}
				}
				this.messages = threadMessages
			} else {
				this.messages = getMessages()
			}
		},

		calculateReadReceipts() {
			const messageIds =
				this.subscriptionType === 'thread'
					? getMessageIdsForBubble(this.chatId)
					: getMessageIds()
			if (!messageIds.length) return {}

			const memberships =
				this.subscriptionType === 'thread'
					? getMembershipsForBubble(this.chatId)
					: getMemberships()

			const userIdsByMessageId = memberships.reduce((acc, memb) => {
				if (memb.user_id === store.auth.user.id) return acc
				acc[memb.mark] = (acc[memb.mark] || []).concat(memb.user_id)
				return acc
			}, {})

			const receiptsForKnownMessages = messageIds.reduce((acc, msgId) => {
				acc[msgId] = Object.values(userIdsByMessageId[msgId] || {})
				return acc
			}, {})

			Object.entries(userIdsByMessageId).forEach(([msgId, receipts]) => {
				if (receiptsForKnownMessages[msgId]) return
				const nextKnownMsgIndex = messageIds.findIndex((id) => id > msgId)
				let indexOfIdToAppendTo = 0
				if (nextKnownMsgIndex === -1) {
					indexOfIdToAppendTo = messageIds.length - 1
				} else if (nextKnownMsgIndex > 1) {
					indexOfIdToAppendTo = nextKnownMsgIndex - 1
				} else if (nextKnownMsgIndex === 0) {
					return
				}
				const idToAppendTo = messageIds[indexOfIdToAppendTo]
				receiptsForKnownMessages[idToAppendTo].push(...Object.values(receipts))
			})
			return receiptsForKnownMessages
		},

		async focusEditorExitAccessibility() {
			this.accessibilityMode = false
			await sleep(100)
			this.setMessageInputFocus()
		},

		async jumpToLatest() {
			this.isJumpingToLatest = true
			await sleep(1)
			const { latest_message_id } = getStat(this.chatId)
			const latestMessage = getMessage(latest_message_id)
			if (!latestMessage) {
				this.loadingMessages = true
				const latest = await fetchHistoryWithoutStore({
					bubble_id: this.chatId,
				})
				setMessages(latest.messages)
				setMessages(latest.parentmessages)
			}
			this.centeredId = null
			this.forwardHistoryComplete = true
			setStopCursorForBubble(this.chatId, latest_message_id)
			await this.$nextTick()
			const latestMsgElem = document.getElementById(
				`${this.idPrefix}message_${latest_message_id}`
			)
			if (latestMsgElem) {
				latestMsgElem.scrollIntoView({ behavior: 'smooth' })
			} else {
				this.messageList.scrollTop = 0
			}
			deleteOldestMessagesForBubble(this.chatId)
			const [startId] = getSortedMessageIdsForBubble(this.chatId)
			setStartCursorForBubble(this.chatId, startId)
			await sleep(100)
			this.loadingMessages = false
			this.isJumpingToLatest = false
		},

		openThread(message) {
			this.threadId = message.threadId
			if (!this.accessibilityMode) EventBus.$emit('focus-editor')
		},

		openReplyThread(message) {
			EventBus.$emit('prepend-mention', message.user_id)
			this.openThread(message)
		},

		openCreateThread(message) {
			this.$emit('create-thread', message)
		},

		async handleLoadBackward() {
			await this.$nextTick()
			if (!this.loadingMessages && this.loaded && !this.isJumpingToLatest) {
				this.loadNextPageOfHistory()
			}
		},

		async handleLoadForward() {
			if (
				this.forwardHistoryComplete ||
				this.loadingMessages ||
				!this.loaded ||
				!this.messages.length
			)
				return

			this.loadingMessages = true
			this.initialLoad = true
			this.messagesLoaded = true

			try {
				const origScrollHeight = this.messageList.scrollHeight
				const oldest = getStopCursorForBubble(this.chatId)
				const { messages } = await fetchHistory({
					bubble_id: this.chatId,
					oldest,
				})
				await sleep(25)
				const newScrollTop = origScrollHeight - this.messageList.scrollHeight
				this.messageList.scrollTop = newScrollTop
				if (messages.length < MESSAGE_PAGE_SIZE) {
					setTimeout(() => {
						this.forwardHistoryComplete = true
					}, 100)
				}
			} catch (e) {
				console.error(e)
			} finally {
				this.loadingMessages = false
				this.initialLoad = false
				this.messagesLoaded = false
			}
		},

		async connectPusher() {
			if (!this.store.auth.user.hasactivity) {
				return
			}
			if (this._connectingPusher) return
			this._connectingPusher = true

			const subscribePromise = subscribeToPrivateBubble(this.chatId)

			// If we lose the race (timeout / unmount) the underlying Pusher
			// subscribe may still complete, leaving the channel in pusher._channels
			// with no retainChannel ref. retain+release routes through the
			// refcount-aware path so concurrent subscribers aren't disturbed.
			const balanceOrphanedSubscription = () => {
				subscribePromise.then(
					(channel) => {
						if (!channel?.name) return
						retainChannel(channel.name)
						releaseChannel(channel.name)
					},
					() => {}
				)
			}

			try {
				const timeout = new Promise((_, reject) => {
					this._pusherTimeoutId = setTimeout(
						() => reject(new Error('Pusher subscription timeout')),
						10000
					)
				})
				const unmountSignal = new Promise((_, reject) => {
					this._pusherUnmountReject = reject
				})
				const channel = await Promise.race([
					subscribePromise,
					timeout,
					unmountSignal,
				]).finally(() => {
					clearTimeout(this._pusherTimeoutId)
					this._pusherUnmountReject = null
				})

				if (this._unmounted) {
					balanceOrphanedSubscription()
					return
				}

				this.channelName = channel.name
				this.pusherChannel = channel
				retainChannel(channel.name)

				// Skip rebinding when our handlers from a previous connect are
				// still attached (unload() skips unbind while another panel
				// shares the channel) — rebinding would stack duplicate handler
				// sets. The epoch check catches the same channel object coming
				// back from a resubscribe that blanket-unbound it, where we DO
				// need to rebind.
				// Only covers reconnects to the SAME channel: switching to
				// another chat and back still stacks a second handler set on a
				// shared channel (pre-existing). The real fix is per-consumer
				// disposers from bindChannelEventWithChunking, which would
				// retire this guard and the refCount-gated unbind in unload().
				const alreadyBound =
					this._boundChannel === channel &&
					this._boundEpoch === getBindEpoch(channel)
				if (!alreadyBound) {
					channel.bind('unavailable', this.pusherDisconnect)

					// Bind events directly to THIS channel (not globally)
					// This allows multiple ChatPanels to coexist with their own handlers
					this.bindChannelEvents(channel)
					this._boundChannel = channel
					this._boundEpoch = getBindEpoch(channel)
				}

				this.connectedToPusher = true
			} catch (e) {
				balanceOrphanedSubscription()
				if (this._unmounted) return
				this.connectedToPusher = false
				log('connectPusher failed', e)
			} finally {
				this._connectingPusher = false
			}
		},

		bindChannelEvents(channel) {
			// Bind app events to this specific channel
			bindChannelAppEvents(channel, {
				ReactionAdded: this.reactionAddedHandler.bind(this),
				ReactionRemoved: this.reactionRemovedHandler.bind(this),
				MessageTransAdded: this.messageTransAddedHandler.bind(this),
				MarkUpdated: this.markUpdatedHandler.bind(this),
				VideosessionUpdated: this.videosessionUpdatedHandler.bind(this),
				PollUpdated: this.pollUpdatedHandler.bind(this),
			})

			// REST-style message events (flat payload, no App\Events\ prefix)
			bindChannelRestEvents(channel, {
				MessageCreated: this.messageAddedHandler.bind(this),
				MessageUpdated: this.messageUpdatedHandler.bind(this),
				MessageDeleted: this.messageRemovedHandler.bind(this),
			})

			// Bind client events
			bindChannelClientEvents(channel, {
				UserTyping: this.userTypingHandler.bind(this),
				UserStoppedTyping: this.userStoppedTypingHandler.bind(this),
			})
		},

		pusherDisconnect() {
			console.debug(
				'Disconnect to private bubble channel in ChatPanel - will attempt to reconnect'
			)
			this.load(this.chatId)
		},

		pusherReconnect() {
			if (this.hasPreviouslyConnected) {
				console.debug(
					`[dormancy] pusherReconnect debounced for chat=${this.chatId}`
				)
				clearTimeout(this._reconnectTimer)
				this._reconnectTimer = setTimeout(() => {
					console.debug(
						`[dormancy] pusherReconnect firing load() for chat=${this.chatId}`
					)
					this.load(this.chatId)
				}, 300)
			}
			this.hasPreviouslyConnected = true
		},

		async load(id) {
			if (isNaN(parseInt(id)) || !id) {
				console.debug(`Load function passed bad ID ${id}`)
				// Still show UI even without valid data (for dev/testing)
				this.loadingMessages = false
				this.loaded = true
				this.historyComplete = true
				return
			}
			this.unload()

			const chatId = parseInt(id)

			if (this.centeredMessageId) {
				this.centeredId = this.centeredMessageId
			} else {
				this.forwardHistoryComplete = true
			}

			if (!this.centeredId) {
				store.dbLoadChat?.(chatId)

				setTimeout(() => {
					this.messagesLoaded = false
					this.$nextTick(() => {
						setTimeout(() => {
							this.initialLoad = true
						}, 1000)
					})
				}, 1000)
			}

			this.$refs.messageInput?.setFocus()

			// Wait until the chat is available (but don't loop forever)
			const chatData = getChat(chatId)
			if (!chatData || !Object.keys(chatData).length) {
				setTimeout(() => {
					this.load(id)
				}, 100)
				return
			}

			this.currentMark = 0
			this.threadId = null

			const stat = getStat(chatId)
			Object.assign(this, {
				initialMark: stat?.mark,
				latest_message_id: stat?.latest_message_id,
			})

			this.loadingMessages = true

			this._loadGeneration = (this._loadGeneration || 0) + 1
			const gen = this._loadGeneration

			try {
				await this.connectPusher()
				if (gen !== this._loadGeneration) {
					console.debug(
						`[dormancy] load() canceled after connectPusher (gen=${gen} current=${this._loadGeneration})`
					)
					return
				}

				this.loaded = true

				this.loadChatInfo()

				if (this.centeredId) {
					await this.loadCenteredHistory(this.centeredId)
					if (gen !== this._loadGeneration) {
						console.debug(
							`[dormancy] load() canceled after centeredHistory (gen=${gen} current=${this._loadGeneration})`
						)
						return
					}
					this.centeredId = null
				} else {
					await this.loadMessageHistory()
					if (gen !== this._loadGeneration) {
						console.debug(
							`[dormancy] load() canceled after messageHistory (gen=${gen} current=${this._loadGeneration})`
						)
						return
					}
				}

				this.$emit('loaded')
			} finally {
				if (gen === this._loadGeneration) {
					this.loadingMessages = false
				}
			}

			if (this.subscriptionType === 'main') {
				storage.set('lastActiveGroupId', chatId)
			}

			const currentChat = getChat(chatId)
			if (currentChat?.videosession_id) {
				const videosession = getVideosession(currentChat.videosession_id)
				if (videosession && videosession.status == 'ACTIVE') {
					this.store.livestreaming.videosession = videosession
				}
			}

			if (!sStorage.has(`pronto_marked_unread_${chatId}`)) {
				const chat = getChat(chatId)
				if (chat?.marked_unread) {
					markChatAsRead(chat.id)
				}
			}

			this.checkForSuperGroupAlert()
		},

		unload() {
			this.messagesLoaded = true
			this.initialLoad = false
			this.historyComplete = false
			this.forwardHistoryComplete = false
			this.centeredId = null
			this.initialMark = null
			this.unreadCount = 0
			deleteOldestMessagesForBubble(this.chatId)
			this.messages = []
			this.showListDrawer = false

			// Safe to gate on hasactivity — connectPusher() early-returns when
			// hasactivity is false, so retainChannel is never called in that case.
			if (this.store.auth.user.hasactivity && this.channelName) {
				// Explicitly remove all handlers we bound to this channel.
				// Without this, bound handlers (reactionAddedHandler.bind(this),
				// messageAddedHandler.bind(this), etc.) retain `this` as long as
				// the channel object survives — which it may briefly after
				// unsubscribe due to internal Pusher queue state.
				// Skip when another consumer still holds the channel: unbind()
				// removes ALL handlers, including theirs.
				if (getChannelRefCount(this.channelName) <= 1) {
					// bumps the bind epoch even if unbind throws, so the next
					// connectPusher() knows to rebind
					unbindChannel(this.pusherChannel)
					// only cleared when we actually unbound: on the shared-
					// channel path our handlers are still attached, and
					// clearing this would make the next connectPusher() rebind
					// and stack a duplicate set (unload() runs on reconnect
					// cycles, not just destroy)
					this._boundChannel = null
				}
				releaseChannel(this.channelName)
				this.channelName = null
				this.pusherChannel = null
			}

			Object.values(this.typingUsers).forEach(({ timeout }) => {
				window.clearTimeout(timeout)
			})

			this.typingUsers = {}

			this.loaded = false
			this.loadingMessages = false
		},

		async loadParentChatInfo() {
			if (this.subscriptionType !== 'thread') return
			const parentId = this.mentionChatId
			if (!parentId) return
			try {
				await fetchChat(parentId, { include: 'memberships' })
			} catch (e) {
				console.warn('Failed to load parent chat for thread', parentId, e)
			}
		},

		async loadChatInfo() {
			try {
				let chatId = this.chatId

				this.loadInfoAbortController?.abort()
				this.loadInfoAbortController = new AbortController()

				this.loadParentChatInfo()

				const membership = await fetchMyMembershipForChat(
					chatId,
					{},
					{
						signal: this.loadInfoAbortController.signal,
					}
				)
				const { chat, stats } = membership
				if (chat?.id !== chatId) return false

				this.hasThreadMembership = true
				Object.assign(this, {
					unread: stats.unread,
					latest_message_id: stats.latest_message_id,
					initialMark: stats.mark,
					currentMark: stats.mark,
					pendingMark: stats.mark,
				})
				this.chatInfoError = 0
			} catch (e) {
				if (isCanceled(e)) return
				if (this.subscriptionType === 'thread' && e.response?.status === 404) {
					this.hasThreadMembership = false
					await fetchChat(this.chatId, {
						include: 'source_message,memberships',
					})
					return
				}
				this.chatInfoError += 1
			}
		},

		async loadNextPageOfHistory() {
			this.loadingMessages = true
			const start = getStartCursorForBubble(this.chatId)
			await this.loadMessageHistory(start)
			this.loadingMessages = false
		},

		async loadMessageHistory(latest) {
			if (this.historyComplete) {
				return
			}

			let bubbleId = this.chatId
			let data = { bubble_id: bubbleId }

			const log = timedLogger(`bubble history ${bubbleId}`)

			if (!this.messageList) {
				await sleep(500)
				return this.loadMessageHistory(latest)
			}

			if (latest) {
				data.latest = latest
			}

			if (this.loadHistoryCancelToken) {
				this.loadHistoryCancelToken.cancel()
				this.loadHistoryCancelToken = null
			}

			this.loadHistoryCancelToken = getCancelToken()

			try {
				log('starting fetch...')
				const { messages } = await fetchHistory(
					data,
					this.loadHistoryCancelToken
				)
				log('fetch complete')
				if (bubbleId != this.chatId) {
					return
				}

				if (messages.length == 0 || messages.length < MESSAGE_PAGE_SIZE) {
					this.historyComplete = true
				}

				this.messagesLoaded = true

				this.waitingForBannerCheck = true

				this.updateMark()

				this.bubbleHistoryError = 0
			} catch (error) {
				if (isCanceled(error)) return
				log('history fail')
				console.error('BUBBLE.HISTORY ERROR', error)

				this.bubbleHistoryError += 1
			}
			log.print()
		},

		checkForSuperGroupAlert() {
			if (!this.chatId) return
			const chat = getChat(this.chatId)
			const membership = getMyMembershipForBubble(this.chatId)
			if (chat?.is_supergroup && !membership?.supergroup_alert_seen) {
				setTimeout(() => {
					this.showSuperGroupModal = true
				}, 1000)
			}
		},

		async dismissSuperGroupAlert() {
			this.showSuperGroupModal = false
			const chat = getChat(this.chatId)
			const membership = getMyMembershipForBubble(this.chatId)
			if (chat?.is_supergroup && !membership?.supergroup_alert_seen) {
				await updateMembership({
					bubble_id: this.chatId,
					supergroup_alert_seen: true,
				})
			}
		},

		async checkIfUnreadBannerShouldDisplay() {
			if (this.threadId || !this.loaded) return false
			const stat = getStat(this.chatId)
			if (!stat?.unread) return false

			await this.$nextTick()
			const divider = document.getElementById('new-msg-divider')
			if (divider) {
				const inView = await asyncIsElementInView(divider, this.messageList)
				if (inView) return false
				this.unreadCount = stat.unread
				return true
			}

			if (!this.initialMark) return false

			const startCursorId = getStartCursorForBubble(this.chatId)
			if (startCursorId > stat.mark) {
				this.unreadCount = stat.unread
				return true
			}

			return false
		},

		dismissUnreadBanner() {
			console.debug('ChatPanel: dismissing unread banner')
			const stat = getStat(this.chatId)
			Object.assign(this, {
				unreadCount: 0,
				initialMark: stat?.mark,
				newMessageLoader: false,
			})
		},

		async scrollToLatestUnread() {
			let unreadDivider = document.getElementById('new-msg-divider')
			if (!unreadDivider) {
				this.newMessageLoader = true
				await this.loadCenteredHistory(this.initialMark)
				await this.$nextTick()
				unreadDivider = document.getElementById('new-msg-divider')
			}
			this.dismissUnreadBanner()
			unreadDivider?.scrollIntoView({ behavior: 'smooth' })
		},

		async loadCenteredHistory(messageId) {
			try {
				const { latest_message_id } = getStat(this.chatId)
				const result = await this.centered.load(this.chatId, messageId)
				if (!result) return
				const { older, newer } = result
				await sleep(250)
				this.loadingMessages = false
				importMessagesFromCacheForBubble(this.chatId, true)
				if (older.messages.length < MESSAGE_PAGE_SIZE) {
					this.historyComplete = true
				}
				this.forwardHistoryComplete =
					(newer.messages.length < 1 ||
						newer.messages[0]?.id === latest_message_id) ??
					false
				this.centered.scrollIntoView(messageId, this.idPrefix)
				await this.$nextTick()
				const latest = await fetchHistoryWithoutStore({
					bubble_id: this.chatId,
					latest: latest_message_id,
				})
				setMessages(latest.messages)
				setMessages(latest.parentmessages)
			} catch (e) {
				console.error(e)
			}
		},

		updateUserList(data) {
			this.userList = data
			this.showListDrawer = true
		},

		async beforeSendHandler() {
			if (this.subscriptionType === 'thread' && !this.hasThreadMembership) {
				await joinThread(this.chatId, getMyUserId())
				this.hasThreadMembership = true
				this.loadChatInfo()
			}
		},

		messageSentHandler() {
			if (!this.channelName) return
			const channel = this.$pusher.channel(this.channelName)
			channel?.trigger('client-App\\Events\\UserStoppedTyping', {
				user_id: getMyUserId(),
			})
		},

		messageCreatedHandler() {
			this.$refs.screenreader.innerHTML = getOldestMessage()
		},

		pollUpdatedHandler({ poll }) {
			updateMessageWithPoll(poll)
		},

		updateMark() {
			const latestMessage =
				this.subscriptionType === 'thread'
					? getMostRecentMessageForBubble(this.chatId)
					: getMostRecentMessage()
			const { appVisible, userIsActive } = this.store
			if (
				!this.loaded ||
				!this.messages.length ||
				this.threadId ||
				!appVisible ||
				!userIsActive ||
				!latestMessage ||
				this.currentMark >= latestMessage.id ||
				this.pendingMark >= latestMessage.id ||
				(this.subscriptionType === 'thread' && !this.hasThreadMembership)
			) {
				return
			}

			this.pendingMark = latestMessage.id

			post('/api/v1/bubble.mark', {
				bubble_id: this.chatId,
				message_id: latestMessage.id,
			})
				.then(({ data }) => {
					if (data.ok) {
						closeNotificationsForBubble(this.chatId)
						if (!this.showUnreadBanner) {
							this.initialMark = latestMessage.id
						}
						if (latestMessage.id > this.currentMark) {
							this.currentMark = latestMessage.id
							updateStat(this.chatId, {
								unread: 0,
								unread_mentions: 0,
								mark: latestMessage.id,
							})
							this.unreadCount = 0
						}
					} else if (this.pendingMark === latestMessage.id) {
						this.pendingMark = this.currentMark
					}
				})
				.catch((error) => {
					console.error(error)
					if (this.pendingMark === latestMessage.id) {
						this.pendingMark = this.currentMark
					}
				})
		},

		typingHandler() {
			if (!this.channelName) return
			const timeSinceLastEvent = Date.now() - (this.lastTypingSentTime || 0)
			if (timeSinceLastEvent >= USER_TYPING_EVENT_INTERVAL_MS) {
				this.lastTypingSentTime = Date.now()
				const channel = this.$pusher.channel(this.channelName)
				channel?.trigger('client-App\\Events\\UserTyping', {
					user_id: getMyUserId(),
					thread_id: this.threadId || null,
				})
			}
		},

		messageAddedHandler(message) {
			setMessageAndMoveCursor(message)

			if ((message.chat_id || message.bubble_id) !== this.chatId) return false

			if (this.store.appVisible) {
				this.updateMark()
			}
		},

		messageUpdatedHandler(message) {
			setMessage(message)
		},

		reactionAddedHandler({ message_id, emoji, emoji_base, user_id, count }) {
			const me = user_id === getMyUserId()
			const message = getMessage(message_id)
			if (!message) return
			const currentReactions = { ...message.reactions }
			const existingReaction = currentReactions[emoji]

			if (existingReaction) {
				currentReactions[emoji] = {
					...existingReaction,
					count,
					me: me || existingReaction.me,
					users: me
						? [...existingReaction.users, user_id]
						: existingReaction.users,
					emoji,
					emoji_base,
				}
			} else {
				currentReactions[emoji] = {
					users: [user_id],
					me,
					count,
					emoji,
					emoji_base,
				}
			}

			updateMessage(message.id, {
				reactions: currentReactions,
			})
		},

		reactionRemovedHandler({ message_id, emoji, emoji_base, user_id, count }) {
			const me = user_id === getMyUserId()
			const message = getMessage(message_id)
			if (!message) return
			const currentReactions = { ...message.reactions }

			if (count === 0) {
				delete currentReactions[emoji]
			} else {
				const reaction = currentReactions[emoji]
				if (reaction) {
					currentReactions[emoji] = {
						...reaction,
						count,
						me: me ? false : reaction.me,
						users: me ? [] : [user_id],
						emoji,
						emoji_base,
					}
				}
			}

			updateMessage(message.id, {
				reactions: currentReactions,
			})
		},

		messageTransAddedHandler({ messagetrans }) {
			const me = getMyUser()
			if (messagetrans.lang === me.language) {
				updateMessage(messagetrans.message_id, { messagetrans })
			}
		},

		messageRemovedHandler({ id }) {
			const message = getMessage(id)
			// If message is in store but belongs to a different chat, ignore.
			// If not in store, allow the delete (no-op for missing messages).
			if (message && message.bubble_id !== this.chatId) return
			deleteMessage(id)
		},

		markUpdatedHandler({ user_id, mark }) {
			updateMembershipForUser(user_id, { mark }, this.chatId)
		},

		userTypingHandler(payload) {
			const { user_id, thread_id } = payload
			if (isMyUser(user_id)) return

			const { timeout } = this.typingUsers[user_id] || {}
			window.clearTimeout(timeout)

			this.typingUsers[user_id] = {
				thread_id,
				user_id,
				timeout: setTimeout(() => {
					this.userStoppedTypingHandler({ user_id })
				}, 5000),
			}
		},

		userStoppedTypingHandler({ user_id }) {
			const { [user_id]: data, ...rest } = this.typingUsers
			window.clearTimeout(data?.timeout)
			this.typingUsers = rest
		},

		videosessionUpdatedHandler({ videosession }) {
			if (videosession.id === this.store.livestreaming.videosession.id) {
				this.store.livestreaming.videosession = videosession
			}

			const msgs = this.messages.filter(
				(msg) => msg.videosession_id === videosession.id
			)

			msgs.forEach((message) => {
				updateMessage(message.id, { videosession })
			})

			if (
				videosession.bubble_id === this.chatId ||
				videosession.chat_id === this.chatId
			) {
				setVideosession(videosession)
			}
		},

		setMessageInputFocus() {
			this.$refs.messageInput?.setFocus()
		},

		clearFail() {
			this.showFail = false
			this.showRefresh = false
		},

		reloadPage() {
			window.location.reload(true)
		},

		pinnedBannerClick() {
			const pinnedId = this.currentChat?.pinned_message_id
			if (!pinnedId) return
			const pinnedMsg = getMessage(pinnedId)
			if (pinnedMsg) {
				pinnedMsg.threadId = pinnedId
				this.openThread(pinnedMsg)
			}
		},

		onDropFiles(e) {
			this.$refs.messageInput?.onDropFiles(e)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.chat-panel {
	overflow-y: hidden;
	flex: 1;
	display: flex;
	flex-direction: column;
	position: relative; // Contain absolutely positioned children like LoadingBar

	:deep(.loading-bar) {
		width: 100%;
		&:before {
			width: 100%;
		}
	}
}

.status-bar-wrapper {
	position: relative;
}

.message-list-main {
	z-index: 1;
	opacity: 1;
	transition: all 0.5s ease-in-out;
	transition-property: opacity, filter;
	&.hide-chat {
		opacity: 0;
		filter: blur(15px);
		pointer-events: none;
	}
}

.status-bar {
	left: 0;
	bottom: 1px;
	position: absolute;
	background-color: var(--neutral-0);
	object-fit: contain;
	font-family: Avenir-Roman;
	font-size: 12px;
	text-align: left;
	@include flex;
	color: var(--neutral-500);
	z-index: 2;
	transition: all 0.3s ease-in-out;
	transition-property: height, opacity;
	height: 0;
	opacity: 0;
	overflow: hidden;
	// stop at the content edge rather than the panel edge: 16px matches
	// .message-list's padding-right (MessageList.vue), so this still masks
	// everything scrolling behind it while leaving the scrollbar clear
	right: 16px;
	padding-left: 30px;
	&.status-bar-expanded {
		height: 25px;
		opacity: 1;
	}
}

.message-list-wrapper {
	position: relative;
	flex-grow: 1;
	overflow: auto;
	display: flex;
	border-bottom: solid 1px var(--neutral-40040);
}

.message-footer {
	flex-shrink: 0;
	background: var(--neutral-0);
}

.messages-loader {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 0;
}

.no-messages {
	position: absolute;
	top: calc(50% - 20px);
	font-family: Avenir-Medium;
	font-size: 16px;
	color: var(--neutral-400);
	left: 0;
	right: 0;
	text-align: center;
}

.no-scroll {
	overflow: hidden;
}

.pinned-banner {
	top: 75px !important;
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.chat-panel {
		.message-list-wrapper {
			background: var(--neutral-100) !important;
			.thread-message-list {
				.online-indicator {
					border-color: var(--neutral-300);
				}
				.message-wrapper {
					&.message-hover {
						.online-indicator {
							border-color: var(--neutral-0);
						}
					}
				}
			}
		}
		.status-bar {
			background: var(--neutral-100);
		}
	}
}
</style>
